import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { bool, filterBy } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { sortByCaseInsensitive, SortDirection } from 'cio/utils/sort';

import AnalyticsService from 'cio/services/analytics';
import ApiErrorsService from 'cio/services/api-errors';
import CollectionModel from 'cio/models/collection';
import SettingsService from 'cio/services/settings';
import { DEFAULT_MAX_COLLECTION_COUNT } from 'cio/utils/collections';
import AccountService from 'cio/services/account';
import { tracked } from '@glimmer/tracking';

const DEFAULT_VALUES = {
  sort: 'name',
  asc: false,
  showAddCollectionModal: false,
};

export default class CollectionsIndexController extends Controller {
  @service analytics!: AnalyticsService;
  @service apiErrors!: ApiErrorsService;
  @service settings!: SettingsService;
  @service account!: AccountService;

  @tracked searchTerm = '';

  declare model: CollectionModel[];

  queryParams = ['sort', 'asc'];
  sort = DEFAULT_VALUES.sort;
  asc = DEFAULT_VALUES.asc;

  maxCollectionCount = DEFAULT_MAX_COLLECTION_COUNT;
  showAddCollectionModal = DEFAULT_VALUES.showAddCollectionModal;

  @bool('account.current.suspendTrialAt') isEssentialsTrial!: boolean;

  @filterBy('model', 'isNew', false) collections!: CollectionModel[];

  @computed('model.length', 'settings.environmentSettings.maxCollectionSize')
  get reachedCollectionLimit() {
    let max = this.settings.environmentSettings.maxCollectionSize ?? DEFAULT_MAX_COLLECTION_COUNT;

    return this.model.length >= max;
  }

  @computed('sort', SortDirection.Ascending, 'matchedCollections.[]')
  get sortedCollections() {
    let sorted = sortByCaseInsensitive(this.matchedCollections, [this.sort as keyof CollectionModel]);
    return this.asc ? sorted.reverse() : sorted;
  }

  setDefaults() {
    this.setProperties(DEFAULT_VALUES);
  }

  get isGatedFeature(): boolean {
    return this.account.planFeatures.collections.enabled === false;
  }

  @computed('searchTerm', 'collections.[]')
  get matchedCollections() {
    let trimmedSearchTerm = this.searchTerm.trim().toLowerCase();
    if (!trimmedSearchTerm) {
      return this.collections;
    }

    return this.collections.filter(({ name }) => name.toLowerCase().includes(trimmedSearchTerm));
  }

  handleAddCollectionClick = (popoverOpen: () => unknown) => {
    if (this.isGatedFeature) {
      popoverOpen();
    } else {
      this.revealAddCollectionModal();
    }
  };

  saveCollectionTask = task({ drop: true }, async (collection: CollectionModel) => {
    try {
      await collection.save();
    } catch (error) {
      this.apiErrors.report(error);
    }

    this.set('showAddCollectionModal', false);
  });

  @action
  setSort(sortBy: string, sortDirection: SortDirection) {
    this.setProperties({
      sort: sortBy,
      asc: sortDirection === SortDirection.Ascending,
    });
  }

  @action
  revealAddCollectionModal() {
    this.set('showAddCollectionModal', true);
    this.analytics.track('collections', 'creation_started');
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'collections/index': CollectionsIndexController;
  }
}
